<template>
  <div>
    <div class="ma-4 px-4">
      <v-sheet outlined>
        <v-data-table
          :headers="departmentHeaders"
          :items="departmentsList"
          fixed-header
          :items-per-page="5"
          hide-default-footer
          class="secondary row-height-50"
        >
          <template #[`item.action`]="{ item }">
            <v-icon
              small
              class="mr-2"
              color="accent"
              @click="editDepartment(item)"
              :disabled="!item.active"
            >
              mdi-pencil
            </v-icon>
          </template>
          <template #[`item.active`]="{ item }">
            <v-chip
              small
              :color="item.active ? 'green' : 'red'"
              dark
              @click="openConfirm(item, 'department')"
            >
              {{ showActiveStatus(item.active) }}
            </v-chip>
          </template>
        </v-data-table>
      </v-sheet>
      <div class="pt-6">
        <span class="text-5xl font-weight-bold accent--text"> PROGRAMS </span>
      </div>
      <searchBar
        @add="(programDialog = true), (editProgramMode = false)"
        @updateSearch="(v) => (searchProgram = v)"
        headerText=""
        class="mt-2"
        @fileUploaded="initialise"
      />

      <v-row>
        <v-col cols="12">
          <v-sheet outlined>
            <v-data-table
              :headers="programHeaders"
              :search="searchProgram"
              :items="programs"
              fixed-header
              :items-per-page="5"
              hide-default-footer
              class="secondary row-height-50"
            >
              <template #[`item.action`]="{ item }">
                <v-icon
                  small
                  class="mr-2"
                  color="accent"
                  :disabled="!item.active"
                  @click="editProgram(item)"
                >
                  mdi-pencil
                </v-icon>
              </template>
              <template #[`item.degree_specialization`]="{ item }">
                {{ item.degree?.short_name }}
                {{ item.specialization ? "- " + item.specialization : "" }}
              </template>
              <template #[`item.duration`]="{ item }">
                {{ item.duration }} {{ getDuration(item) }}
              </template>
              <template #[`item.active`]="{ item }">
                <v-chip
                  small
                  :color="item.active ? 'green' : 'red'"
                  dark
                  @click="openConfirm(item, 'program')"
                >
                  {{ showActiveStatus(item.active) }}
                </v-chip>
              </template>
              <template v-if="programs.length > 5" v-slot:footer>
                <div class="d-flex text--accent justify-end mx-3 pa-3">
                  <span
                    @click="navigate('programs')"
                    style="
                      cursor: pointer;
                      color: #4f4584;
                      text-decoration: underline;
                    "
                    >View More</span
                  >
                </div>
              </template>
            </v-data-table>
          </v-sheet>
        </v-col>
      </v-row>
    </div>
    <div class="ma-4 px-4" v-if="currentUser.role == 'Partner'">
      <div class="pt-2">
        <span class="text-5xl font-weight-bold accent--text"> COURSES </span>
      </div>
      <searchBar
        @add="(courseDialog = true), (editCourseMode = false)"
        @updateSearch="(v) => (searchCourse = v)"
        headerText=""
        class="mt-2"
        @fileUploaded="initialise"
      />

      <v-row>
        <v-col cols="12">
          <v-sheet outlined>
            <v-data-table
              :headers="courseHeaders"
              :search="searchCourse"
              :items="courses"
              :custom-filter="customFilter"
              :items-per-page="5"
              hide-default-footer
              fixed-header
              class="secondary row-height-50"
            >
              <template #[`item.action`]="{ item }">
                <v-icon
                  small
                  class="mr-2"
                  color="accent"
                  :disabled="!item.active"
                  @click="editCourse(item)"
                >
                  mdi-pencil
                </v-icon>
              </template>
              <template #[`item.active`]="{ item }">
                <v-chip
                  small
                  :color="item.active ? 'green' : 'red'"
                  dark
                  @click="openConfirm(item, 'course')"
                >
                  {{ showActiveStatus(item.active) }}
                </v-chip>
              </template>
              <template #[`item.department_id`]="{ value }">
                {{ getDepartment(value) }}
              </template>
              <template #[`item.objectives`]="{ item }">
                <v-chip
                  small
                  @click="showData(item.name, item.objectives, 'Objective')"
                >
                  View</v-chip
                >
              </template>
              <template #[`item.outcomes`]="{ item }">
                <v-chip
                  small
                  @click="showData(item.name, item.outcomes, 'Outcome')"
                >
                  View</v-chip
                >
              </template>
              <template #[`item.experiments`]="{ item }">
                <div>
                  <v-chip
                    v-if="item.experiments && item.experiments.length"
                    small
                    @click="showData(item.name, item.experiments, 'Experiment')"
                  >
                    View
                  </v-chip>
                  <span v-else>-</span>
                </div>
              </template>
              <template v-if="courses.length > 5" v-slot:footer>
                <div class="d-flex text--accent justify-end mx-3 pa-3">
                  <span
                    @click="navigate('courses')"
                    style="
                      cursor: pointer;
                      color: #4f4584;
                      text-decoration: underline;
                    "
                    >View More</span
                  >
                </div>
              </template>
            </v-data-table>
          </v-sheet>
        </v-col>
      </v-row>
    </div>
    <v-dialog v-model="programDialog" width="800" persistent scrollable>
      <formProgramDialog
        v-if="programDialog"
        @closeDialog="programDialog = false"
        @create="createProgram"
        @update="updateProgram"
        :department_id="parseInt(department_id)"
        :program="program"
        :departments="departments"
        :edit="editProgramMode"
      />
    </v-dialog>
    <v-dialog v-model="courseDialog" width="800" persistent scrollable>
      <formCourseDialog
        v-if="courseDialog"
        @closeDialog="courseDialog = false"
        @create="createCourse"
        @update="updateCourse"
        :department_id="parseInt(department_id)"
        :course="course"
        :departments="departments"
        :edit="editCourseMode"
      />
    </v-dialog>
    <v-dialog v-model="departmentDialog" width="800" persistent scrollable>
      <formDepartmentDialog
        v-if="departmentDialog"
        @closeDialog="departmentDialog = false"
        @update="updateDepartment"
        :department="department"
        :edit="editDepartmentMode"
      />
    </v-dialog>
    <v-dialog v-model="showDialog" max-width="600" persistent scrollable>
      <v-card class="secondary">
        <v-card-title class="primary white--text"
          >{{ selectedItem.name }} - {{ selectedItem.content_name }}
          <v-spacer></v-spacer>
          <v-btn small icon @click="showDialog = false">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn></v-card-title
        >
        <v-divider></v-divider>
        <v-card-text class="mt-3 reset-card-text">
          <div v-html="selectedItem.description"></div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <ConfirmDialog
      v-if="showConfirmDialog"
      :show="showConfirmDialog"
      @delete="changeConfirmation"
      @close="showConfirmDialog = false"
      :title="title"
      :name="name"
      :active="currentItem.active"
    />
  </div>
</template>

<script>
import formCourseDialog from "@/components/course/form.vue";
import formProgramDialog from "@/components/program/form.vue";
import formDepartmentDialog from "@/components/department/form.vue";
import ConfirmDialog from "@/components/confirm.vue";

import {
  departmentHeaders,
  courseHeaders,
  programHeaders,
} from "@/constants/headers";
import searchBar from "@/components/searchBar.vue";
import { durations } from "@/constants/enum";
export default {
  components: {
    formCourseDialog,
    formProgramDialog,
    formDepartmentDialog,
    searchBar,
    ConfirmDialog,
  },
  data() {
    return {
      programDialog: false,
      courseDialog: false,
      departmentDialog: false,
      editCourseMode: false,
      editProgramMode: false,
      editDepartmentMode: false,
      department: {},
      deleteDialog: false,
      currentItem: {},
      courses: [],
      course: {},
      programs: [],
      program: {},
      departments: [],
      searchCourse: "",
      searchProgram: "",
      departmentHeaders: departmentHeaders,
      courseHeaders: courseHeaders,
      programHeaders: programHeaders,
      department_id: null,
      durations: durations,
      department_name: null,
      listData: false,
      departmentsList: [],
      selectedItem: {},
      showDialog: false,
      showConfirmDialog: false,
      title: null,
      name: null,
    };
  },
  watch: {},
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
  },
  async created() {
    this.department_id = this.$route.params.id;
    this.courseHeaders = courseHeaders.filter(
      (h) => h.value !== "department_id"
    );

    this.programHeaders = programHeaders.filter(
      (h) => h.value !== "department.name"
    );

    await this.initialise();

    this.department_name = this.getDepartment(this.department_id);
    this.$store.commit("setBreadcrumbs", [
      { text: "Departments", to: { name: "Departments" } },
      { text: this.department_name },
    ]);
  },
  methods: {
    async initialise() {
      await this.getDepartments();
      await this.getCourses();
      await this.getPrograms();
      this.departmentsList = this.departments.filter(
        (x) => x.id == this.department_id
      );
    },
    async getCourses() {
      await this.$axios
        .get("courses")
        .then((res) => {
          const response = res.data.data;
          this.courses = response
            .filter((c) => c.department_id == this.department_id)
            .sort((a, b) => b.id - a.id);
        })
        .catch(async (error) => {
          this.$store.commit("showSnackbar", {
            message: error.response.data.message,
            color: "error",
          });
        });
    },
    async getPrograms() {
      await this.$axios
        .get("programs")
        .then((res) => {
          const response = res.data.data;
          this.programs = response
            .filter((p) => p.department_id == this.department_id)
            .sort((a, b) => b.id - a.id);
        })
        .catch(async (error) => {
          this.$store.commit("showSnackbar", {
            message: error.response.data.message,
            color: "error",
          });
        });
    },
    async getDepartments() {
      await this.$axios
        .get("departments")
        .then((res) => {
          this.departments = res.data.data;
        })
        .catch(async (error) => {
          this.$store.commit("showSnackbar", {
            message: error.response.data.message,
            color: "error",
          });
        });
    },
    async createCourse(data) {
      await this.$axios
        .post("courses", { course: data })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Course created successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    async updateCourse(data) {
      await this.$axios
        .put("courses/" + data.id, { course: data })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Course updated successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    async updateDepartment(data) {
      await this.$axios
        .put("departments/" + data.id, { department: data })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Department updated successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    async createProgram(data) {
      await this.$axios
        .post("programs", { program: data })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Programme created successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    async updateProgram(data) {
      await this.$axios
        .put("programs/" + data.id, { program: data })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Programme updated successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    editCourse(item) {
      this.course = { ...item };
      this.editCourseMode = true;
      this.courseDialog = true;
    },
    editProgram(item) {
      this.program = { ...item };
      this.editProgramMode = true;
      this.programDialog = true;
    },
    editDepartment(item) {
      this.department = { ...item };
      this.editDepartmentMode = true;
      this.departmentDialog = true;
    },

    resetValues() {
      this.editCourseMode = false;
      this.courseDialog = false;
      this.course = {};
      this.editDepartmentMode = false;
      this.departmentDialog = false;
      this.department = {};
      this.editProgramMode = false;
      this.programDialog = false;
      this.program = {};
      this.title = null;
      this.name = null;
      this.showConfirmDialog = false;
      this.currentItem = {};
    },

    getDepartment(value) {
      return this.departments.find((x) => x.id == value)?.name;
    },
    getDuration(item) {
      const durationObj = this.durations.find(
        (p) => p.id == item.duration_type
      );
      if (!durationObj) return ""; // Handle invalid duration_type

      const name = durationObj.name;
      return item.duration > 1 ? `${name}s` : name;
    },
    showActiveStatus(value) {
      return value ? "Active" : "Inactive";
    },
    customFilter(value, search, item) {
      if (!search) return true;
      const activeStatus = this.showActiveStatus(item.active);
      const department = this.getDepartment(item.department_id);
      return (
        JSON.stringify(item).toLowerCase().includes(search.toLowerCase()) ||
        activeStatus.toLowerCase().includes(search.toLowerCase()) ||
        department.toLowerCase().includes(search.toLowerCase())
      );
    },

    navigate(item) {
      this.$router.push({
        path: `/departments/${this.department_id}/${item}`,
        query: {
          name: `${this.department_name}`,
        },
      });
    },
    showData(name, content, content_name) {
      this.showDialog = true;
      this.selectedItem.description = content;
      this.selectedItem.name = name;
      this.selectedItem.content_name = content_name;
    },
    openConfirm(item, title) {
      this.name =
        title == "program"
          ? `${item.degree.short_name} ${
              item.specialization ? " - " + item.specialization : ""
            }`
          : item.name;
      this.title = title;
      this.currentItem = { ...item };
      this.showConfirmDialog = true;
    },
    changeConfirmation() {
      if (this.title == "program") {
        this.updateProgram({
          id: this.currentItem.id,
          active: !this.currentItem.active,
        });
      } else if (this.title == "department") {
        this.updateDepartment({
          id: this.currentItem.id,
          active: !this.currentItem.active,
        });
      } else if (this.title == "course") {
        this.updateCourse({
          id: this.currentItem.id,
          active: !this.currentItem.active,
        });
      }
    },
  },
};
</script>

<style scoped>
.active-row {
  background: linear-gradient(
    90deg,
    #d4f8d480,
    #d4f8d480
  ) !important; /* White to Light Green */
}

.inactive-row {
  background: linear-gradient(
    90deg,
    #ffffff,
    #f5c6cb3d
  ) !important; /* Light Red */
}
</style>
